// Copyright 2025-2026 Florian Gallon. All Rights reserved.
import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { t } from '../translations';
import { getCurrentBakeryId, getBakery } from '../utils/bakeryUtils';
import { validateEmail, validatePhone, validatePassword, validateMatch, validateRequired } from '../utils/formValidationUtils';
import InputField from './forms/InputField';
import PasswordField from './forms/PasswordField';
import Button from './Button';
import Alert from './ui/Alert';
import { privacyPolicyData } from '../data/privacyPolicyData';
import { User, Mail, Phone } from 'lucide-react';

const COUNTRIES = [
  { code: 'FR', name: 'France', nameFr: 'France', currency: 'EUR', symbol: '€', flag: '🇫🇷', phonePlaceholder: '+33 6 12 34 56 78' },
  { code: 'CA', name: 'Canada', nameFr: 'Canada', currency: 'CAD', symbol: 'CA$', flag: '🇨🇦', phonePlaceholder: '+1 (555) 123-4567' },
  { code: 'US', name: 'United States', nameFr: 'États-Unis', currency: 'USD', symbol: '$', flag: '🇺🇸', phonePlaceholder: '+1 (555) 123-4567' },
  { code: 'GB', name: 'United Kingdom', nameFr: 'Grande-Bretagne', currency: 'GBP', symbol: '£', flag: '🇬🇧', phonePlaceholder: '+44 20 1234 5678' }
];

export default function CustomerRegistration({ onSuccess, onSwitchToBaker }) {
  const { language } = useLanguage();
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    country: '',
    privacyPolicyAccepted: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [bakeryName, setBakeryName] = useState('');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const loadBakeryName = async () => {
      const bakeryId = getCurrentBakeryId();
      if (bakeryId) {
        try {
          const bakery = await getBakery(bakeryId);
          if (bakery && bakery.name) {
            setBakeryName(bakery.name);
          }
        } catch (error) {
          console.error('Error loading bakery name:', error);
        }
      }
    };

    loadBakeryName();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrors({ name: '', email: '', phone: '', password: '', confirmPassword: '' });

    // Validate all fields
    const validationErrors = {};
    let hasError = false;

    // Validate country
    if (!formData.country) {
      setError(language === 'fr' ? 'Veuillez choisir un pays' : 'Please choose a country');
      return;
    }

    // Validate name
    const nameValidation = validateRequired(formData.name, t('name', language));
    if (!nameValidation.isValid) {
      validationErrors.name = nameValidation.message;
      hasError = true;
    }

    // Validate email
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      validationErrors.email = emailValidation.message;
      hasError = true;
    }

    // Validate phone
    const phoneValidation = validatePhone(formData.phone, formData.country);
    if (!phoneValidation.isValid) {
      validationErrors.phone = phoneValidation.message;
      hasError = true;
    }

    // Validate password
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      validationErrors.password = passwordValidation.message;
      hasError = true;
    }

    // Validate password confirmation
    const matchValidation = validateMatch(formData.password, formData.confirmPassword, language === 'fr' ? 'Les mots de passe' : 'Passwords');
    if (!matchValidation.isValid) {
      validationErrors.confirmPassword = matchValidation.message;
      hasError = true;
    }

    // Check privacy policy
    if (!formData.privacyPolicyAccepted) {
      setError(t('privacyPolicyRequired', language));
      showToast(t('privacyPolicyRequired', language), 'error');
      return;
    }

    if (hasError) {
      setErrors(validationErrors);
      showToast(t('fillAllFields', language), 'error');
      return;
    }

    setLoading(true);

    try {
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);

      // Create user document
      const userData = {
        email: formData.email,
        name: formData.name,
        phone: formData.phone,
        role: 'customer',
        country: formData.country,
        createdAt: new Date().toISOString(),
        address: '',
        privacyPolicyAcceptedAt: new Date().toISOString(),
        privacyPolicyVersion: '1.0'
      };

      await setDoc(doc(db, 'users', userCredential.user.uid), userData);

      showToast(
        language === 'fr' ? 'Compte créé avec succès!' : 'Account created successfully!',
        'success'
      );

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const errorMessage = err.code === 'auth/email-already-in-use'
        ? (language === 'fr' ? 'Cette adresse email est déjà utilisée' : 'This email is already in use')
        : err.message;
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const privacyContent = privacyPolicyData[language] || privacyPolicyData.en;

  return (
    <>
      <div className="glass-effect rounded-3xl shadow-modern-lg p-8 lg:p-12 w-full max-w-md border border-white/30">
        {/* Bakery-specific logo and name */}
        {bakeryName && (
          <div className="text-center mb-8">
            <div className="inline-block p-6 bg-[var(--bg-secondary)] rounded-3xl shadow-modern-lg mb-6">
              <img src="/Logo-digi-bakery-512.png" alt="Logo" className="w-24 h-24 mx-auto" />
            </div>
            <h1 className="text-3xl lg:text-4xl font-black text-[var(--text-primary)] tracking-tight">{bakeryName}</h1>
            <p className="text-[var(--text-secondary)] mt-4 text-lg font-medium">
              {t('createAccount', language)}
            </p>
          </div>
        )}

        {/* Generic registration title when no bakery */}
        {!bakeryName && (
          <div className="text-center mb-8">
            <div className="inline-block p-6 bg-[var(--bg-secondary)] rounded-3xl shadow-modern-lg mb-6">
              <div className="text-6xl">👤</div>
            </div>
            <h2 className="text-2xl lg:text-3xl font-black text-[var(--text-primary)] tracking-tight">
              {t('signUpButton', language)}
            </h2>
            <p className="text-[var(--text-secondary)] mt-4 text-base font-medium">
              {language === 'fr' 
                ? 'Créez votre compte client pour commander facilement'
                : 'Create your customer account to order easily'
              }
            </p>
          </div>
        )}

        {error && (
          <Alert tone="danger" className="mb-6">
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-bold text-[var(--text-primary)] mb-2">
              {t('countryRegion', language)}
            </label>
            <select
              value={formData.country}
              onChange={(e) => handleInputChange('country', e.target.value)}
              className="w-full border-2 border-[var(--border-subtle)] rounded-2xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-[var(--focus-ring)] focus:border-[var(--action-primary)] transition-all duration-300 bg-[var(--surface-card)]/80 backdrop-blur-sm"
              disabled={loading}
              required
            >
              <option value="">
                {language === 'fr' ? 'Choisissez un pays' : 'Choose a country'}
              </option>
              {COUNTRIES.map(c => (
                <option key={c.code} value={c.code}>
                  {c.flag} {language === 'fr' ? c.nameFr : c.name}
                </option>
              ))}
            </select>
          </div>

          <InputField
            label={t('name', language)}
            type="text"
            value={formData.name}
            onChange={(e) => {
              handleInputChange('name', e.target.value);
              setErrors(prev => ({ ...prev, name: '' }));
            }}
            onBlur={() => {
              if (formData.name) {
                const validation = validateRequired(formData.name, t('name', language));
                if (!validation.isValid) {
                  setErrors(prev => ({ ...prev, name: validation.message }));
                }
              }
            }}
            error={errors.name}
            icon={User}
            placeholder={t('fullName', language) || 'Full Name'}
            disabled={loading}
            required
            autoComplete="name"
          />

          <InputField
            label={t('email', language)}
            type="email"
            value={formData.email}
            onChange={(e) => {
              handleInputChange('email', e.target.value);
              setErrors(prev => ({ ...prev, email: '' }));
            }}
            onBlur={() => {
              if (formData.email) {
                const validation = validateEmail(formData.email);
                if (!validation.isValid) {
                  setErrors(prev => ({ ...prev, email: validation.message }));
                }
              }
            }}
            error={errors.email}
            success={formData.email && !errors.email && validateEmail(formData.email).isValid}
            icon={Mail}
            placeholder="your@email.com"
            disabled={loading}
            required
            autoComplete="email"
          />

          <InputField
            label={t('phone', language)}
            type="tel"
            value={formData.phone}
            onChange={(e) => {
              handleInputChange('phone', e.target.value);
              setErrors(prev => ({ ...prev, phone: '' }));
            }}
            onBlur={() => {
              if (formData.phone) {
                const validation = validatePhone(formData.phone, formData.country);
                if (!validation.isValid) {
                  setErrors(prev => ({ ...prev, phone: validation.message }));
                }
              }
            }}
            error={errors.phone}
            success={formData.phone && !errors.phone && formData.country && validatePhone(formData.phone, formData.country).isValid}
            icon={Phone}
            placeholder={COUNTRIES.find(c => c.code === formData.country)?.phonePlaceholder || '+1 (555) 123-4567'}
            disabled={loading}
            required
            autoComplete="tel"
          />

          <PasswordField
            label={t('password', language)}
            value={formData.password}
            onChange={(e) => {
              handleInputChange('password', e.target.value);
              setErrors(prev => ({ ...prev, password: '' }));
            }}
            onBlur={() => {
              if (formData.password) {
                const validation = validatePassword(formData.password);
                if (!validation.isValid) {
                  setErrors(prev => ({ ...prev, password: validation.message }));
                }
              }
            }}
            error={errors.password}
            disabled={loading}
            required
            showStrengthIndicator={true}
            showRequirements={true}
            autoComplete="new-password"
          />

          <PasswordField
            label={t('confirmPassword', language) || 'Confirm Password'}
            value={formData.confirmPassword}
            onChange={(e) => {
              handleInputChange('confirmPassword', e.target.value);
              setErrors(prev => ({ ...prev, confirmPassword: '' }));
            }}
            onBlur={() => {
              if (formData.confirmPassword) {
                const validation = validateMatch(formData.password, formData.confirmPassword, language === 'fr' ? 'Les mots de passe' : 'Passwords');
                if (!validation.isValid) {
                  setErrors(prev => ({ ...prev, confirmPassword: validation.message }));
                }
              }
            }}
            error={errors.confirmPassword}
            disabled={loading}
            required
            showStrengthIndicator={false}
            showRequirements={false}
            autoComplete="new-password"
          />

          {/* Privacy Policy Checkbox */}
          <div className="flex items-start space-x-3 p-4 bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-subtle)]">
            <input
              type="checkbox"
              id="privacyPolicyAccepted"
              checked={formData.privacyPolicyAccepted}
              onChange={(e) => handleInputChange('privacyPolicyAccepted', e.target.checked)}
              className="mt-1 h-4 w-4 text-[var(--action-primary)] focus:ring-[var(--focus-ring)] border-[var(--border-strong)] rounded"
              disabled={loading}
              required
            />
            <label htmlFor="privacyPolicyAccepted" className="text-sm text-[var(--text-primary)] leading-relaxed">
              {t('privacyPolicyAgreement', language)}
              <button
                type="button"
                onClick={() => setShowPrivacyModal(true)}
                className="text-[var(--action-primary)] hover:text-[var(--action-primary-hover)] underline ml-1 font-medium"
                disabled={loading}
              >
                {t('privacyPolicy', language)}
              </button>
            </label>
          </div>

          <Button
            type="submit"
            loading={loading}
            disabled={loading}
            className="w-full py-4 rounded-2xl shadow-modern-lg hover:shadow-lifted"
          >
            {loading ? (t('pleaseWait', language) || 'Please wait...') : (t('signUpButton', language) || 'Sign Up')}
          </Button>

          <div className="space-y-3">
            <Button
              type="button"
              onClick={() => {
                if (onSwitchToBaker) {
                  onSwitchToBaker();
                }
              }}
              disabled={loading}
              variant="ghost"
              className="w-full text-sm py-3 rounded-xl"
            >
              {language === 'fr' 
                ? 'Vous êtes boulanger ? Créer un compte boulangerie'
                : 'Are you a baker? Create a bakery account'
              }
            </Button>
          </div>
        </form>
      </div>

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--surface-card)] rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-white/20">
            <div className="flex justify-between items-center p-6 border-b bg-[var(--bg-secondary)]">
              <h2 className="text-xl font-bold text-[var(--text-primary)]">{privacyContent.title}</h2>
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] bg-[var(--surface-card)] hover:bg-[var(--bg-secondary)] rounded-full w-8 h-8 flex items-center justify-center text-xl font-bold border border-[var(--border-strong)] transition-colors"
                aria-label="Close privacy policy"
              >
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <p className="text-[var(--text-secondary)] mb-6 text-sm">{privacyContent.effectiveDate}</p>
              <div className="prose prose-gray max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-[var(--text-primary)] leading-relaxed text-sm">
                  {privacyContent.content.trim()}
                </pre>
              </div>
            </div>
            <div className="flex justify-end p-6 border-t bg-[var(--bg-secondary)]">
              <Button
                onClick={() => setShowPrivacyModal(false)}
                className="px-6 py-3 rounded-2xl"
              >
                {language === 'fr' ? 'Fermer' : 'Close'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
